import type { RoomOptions } from '../shared'

// Mirrors validateCriteria / validateCreateOptions / the persistentKey check in
// server/mp-room-server/src/routes/tickets.ts. Throws a plain Error to match the
// production surface: the ticket fetch rejects with a plain Error carrying the
// server's 400 message.
const MAX_CRITERIA_KEYS = 16
const MAX_CRITERIA_VALUE_LENGTH = 256
const MAX_METADATA_KEYS = 32

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

export function validateRoomOptions(opts?: RoomOptions): void {
  if (!opts) return

  if (opts.criteria !== undefined) {
    if (!isPlainObject(opts.criteria)) throw new Error('criteria must be an object')
    const entries = Object.entries(opts.criteria)
    if (entries.length > MAX_CRITERIA_KEYS) throw new Error(`criteria may not exceed ${MAX_CRITERIA_KEYS} keys`)
    for (const [key, value] of entries) {
      if (typeof value !== 'string' && typeof value !== 'number') {
        throw new Error(`criteria value for "${key}" must be a string or number`)
      }
      if (typeof value === 'string' && value.length > MAX_CRITERIA_VALUE_LENGTH) {
        throw new Error(`criteria value for "${key}" is too long`)
      }
    }
  }

  if (opts.createOptions !== undefined) {
    const co = opts.createOptions
    if (!isPlainObject(co)) throw new Error('createOptions must be an object')
    if (co.maxPlayers !== undefined) {
      if (typeof co.maxPlayers !== 'number' || !Number.isInteger(co.maxPlayers) || co.maxPlayers <= 0) {
        throw new Error('createOptions.maxPlayers must be a positive integer')
      }
    }
    if (co.isPrivate !== undefined && typeof co.isPrivate !== 'boolean') {
      throw new Error('createOptions.isPrivate must be a boolean')
    }
    if (co.metadata !== undefined) {
      if (!isPlainObject(co.metadata)) throw new Error('createOptions.metadata must be an object')
      if (Object.keys(co.metadata).length > MAX_METADATA_KEYS) {
        throw new Error(`createOptions.metadata may not exceed ${MAX_METADATA_KEYS} keys`)
      }
    }
  }

  if (opts.persistentKey !== undefined && (typeof opts.persistentKey !== 'string' || opts.persistentKey.length === 0)) {
    throw new Error('persistentKey must be a non-empty string')
  }
}
